<template>
  <div class="container py-3">

    <div class="card shadow-sm">

      <!-- Card Header: Edit / Save toggle only -->
      <div class="card-header d-flex justify-content-end align-items-center gap-3">
        <!-- Add Holiday Button -->
        <button v-if="!isEditMode" class="btn button-default btn-sm" @click="showAddHolidayModal = true">
          <i class="fa fa-plus me-1"></i> Add Holiday
        </button>

        <button v-if="!isEditMode" class="btn button-default btn-sm" @click="enterEditMode">
          <i class="fa fa-edit me-1"></i> Edit Working Days
        </button>

        <button v-if="isEditMode" class="button-cancel" @click="cancelEditMode" :disabled="isSaving">
          <i class="fa fa-times me-1"></i> Cancel
        </button>

        <button v-if="isEditMode" class="btn button-secondary btn-sm" @click="saveCalendar" :disabled="isSaving">
          <span v-if="isSaving"><i class="fa fa-spinner fa-spin me-1"></i>Saving...</span>
          <span v-else><i class="fa fa-save me-1"></i>Save</span>
        </button>
      </div>

      <div class="card-body p-0">
        <div class="calendar-body-grid">

          <!-- Calendar Panel -->
          <div class="calendar-panel">

            <!-- Month Navigation -->
            <div class="d-flex align-items-center justify-content-between mb-3">
              <button class="btn btn-outline-secondary btn-sm nav-btn" @click="prevMonth">
                <i class="fa fa-chevron-left"></i>
              </button>
              <div class="text-center">
                <span class="fw-bold fs-5 text-dark">{{ currentMonthName }}</span>
                <span class="text-muted ms-2">{{ currentYear }}</span>
              </div>
              <button class="btn btn-outline-secondary btn-sm nav-btn" @click="nextMonth">
                <i class="fa fa-chevron-right"></i>
              </button>
            </div>



            <!-- Day Labels -->
            <div class="day-labels-grid mb-1">
              <span
                v-for="day in dayLabels"
                :key="day"
                :class="['day-label', { 'day-label-weekend': day === 'Sat' || day === 'Sun' }]"
              >{{ day }}</span>
            </div>

            <!-- Calendar Grid -->
            <div class="calendar-grid">
              <!-- Empty offset cells -->
              <div v-for="n in firstDayOfMonth" :key="'empty-' + n" class="cal-cell cal-cell-empty"></div>

              <!-- Day cells -->
              <div
                v-for="day in daysInMonth"
                :key="day"
                :class="getDayClasses(day)"
                @click="toggleWorkingDay(day)"
              >
                <span class="day-num">{{ day }}</span>
                <div v-if="getHoliday(day)" class="holiday-types-cell">
                  <span v-if="getHoliday(day).types.includes('poya')"        title="Poya Day">🌙</span>
                  <span v-if="getHoliday(day).types.includes('public')"      title="Public Holiday">🏛️</span>
                  <span v-if="getHoliday(day).types.includes('bank')"        title="Bank Holiday">🏦</span>
                  <span v-if="getHoliday(day).types.includes('mercantile')"  title="Mercantile Holiday">🛍️</span>
                </div>
                <span v-if="isWorkingDay(day)" class="working-dot"></span>
              </div>
            </div>

            <!-- Legend -->
            <div class="legend-row mt-3 pt-3">
              <div class="legend-item">
                <span class="legend-dot" style="background:#198754;"></span>
                <span>Working</span>
              </div>
              <div class="legend-item">
                <span class="legend-dot" style="background:#ffc107;"></span>
                <span>Weekend</span>
              </div>
              <div class="legend-item">
                <span class="legend-dot" style="background:#dee2e6;"></span>
                <span>Off</span>
              </div>
              <div class="legend-item">
                <span>🌙 Poya</span>
              </div>
              <div class="legend-item">
                <span>🏛️ Public</span>
              </div>
              <div class="legend-item">
                <span>🏦 Bank</span>
              </div>
              <div class="legend-item">
                <span>🛍️ Mercantile</span>
              </div>
            </div>
          </div>

          <!-- Side Panel -->
          <div class="side-panel">

            <!-- Holidays This Month -->
            <div class="side-section-title d-flex justify-content-between align-items-center">
              <span><i class="fa fa-star me-1 text-warning"></i> Holidays in {{ currentMonthName }}</span>
              <button
                class="btn btn-link p-0 refresh-btn"
                :disabled="holidaysLoading"
                @click="refreshHolidays"
                title="Refresh holiday data"
              >
                <i :class="['fa', 'fa-refresh', { 'fa-spin': holidaysLoading }]"></i>
              </button>
            </div>

            <!-- Loading -->
            <div v-if="holidaysLoading" class="text-center text-muted py-4">
              <i class="fa fa-spinner fa-spin" style="font-size: 24px; opacity: 0.5;"></i>
              <p class="mt-2 mb-0 small">Loading holidays...</p>
            </div>

            <!-- Error -->
            <div v-else-if="holidaysError" class="sheet-error">
              <i class="fa fa-exclamation-circle me-1"></i>
              <span>{{ holidaysError }}</span>
              <button class="btn btn-sm btn-outline-danger mt-2 w-100" @click="refreshHolidays">Retry</button>
            </div>

            <!-- No data for this year -->
            <div v-else-if="!hasDataForYear" class="text-center text-muted py-4">
              <i class="fa fa-calendar-times-o" style="font-size: 28px; opacity: 0.3;"></i>
              <p class="mt-2 mb-0 small">No holidays found for {{ currentMonthName }} {{ currentYear }}.</p>
            </div>

            <!-- No holidays this month -->
            <div v-else-if="holidaysThisMonth.length === 0" class="text-center text-muted py-4">
              <i class="fa fa-calendar" style="font-size: 28px; opacity: 0.3;"></i>
              <p class="mt-2 mb-0 small">No holidays this month</p>
            </div>

            <div v-else class="holiday-list">
              <div
                v-for="holiday in holidaysThisMonth"
                :key="holiday.date + '-' + holiday.name"
                :class="['holiday-card', `holiday-card-${holiday.primaryType}`, { 'holiday-card-inactive': holiday.is_active === false }]"
                @click="scrollToDay(holiday.day)"
              >
                <div class="holiday-info">
                  <div class="holiday-name">{{ holiday.name }}</div>
                  <div class="holiday-date">{{ formatHolidayDate(holiday.day) }}</div>
                  <div class="holiday-type-icons mt-1">
                    <span v-if="holiday.types.includes('poya')"        class="htype-emoji" title="Poya Day">🌙</span>
                    <span v-if="holiday.types.includes('public')"      class="htype-emoji" title="Public Holiday">🏛️</span>
                    <span v-if="holiday.types.includes('bank')"        class="htype-emoji" title="Bank Holiday">🏦</span>
                    <span v-if="holiday.types.includes('mercantile')" class="htype-emoji" title="Mercantile Holiday">🛍️</span>
                  </div>
                </div>
                <button class="btn btn-sm btn-link p-0 text-secondary ms-2" @click.stop="openEditModal(holiday)" title="Edit Holiday">
                  <i class="fa fa-edit" style="font-size: 14px;"></i>
                </button>
              </div>
            </div>

            <!-- Summary -->
            <div class="side-section-title mt-3">
              <i class="fa fa-list me-1 text-secondary"></i> Summary
            </div>
            <div class="summary-grid">
              <div class="summary-item">
                <span class="summary-label">Total days</span>
                <span class="summary-val">{{ daysInMonth }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Weekends</span>
                <span class="summary-val">{{ weekendDaysCount }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Working</span>
                <span class="summary-val text-success">{{ workingDaysCount }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Holidays</span>
                <span class="summary-val text-danger">{{ holidayDaysCount }}</span>
              </div>
            </div>

            <div v-if="lastSaved" class="d-none"></div>
          </div>

        </div>
      </div>
    </div>

    <!-- Modals -->
    <AddHolidayModal
      v-if="showAddHolidayModal"
      @close="handleModalClose"
      @save="handleModalSave"
    />
    <EditHolidayModal
      v-if="showEditHolidayModal"
      :holidayData="selectedHoliday"
      @close="handleModalClose"
      @update="handleModalSave"
    />
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import AddHolidayModal from "../../Modal/AddHolidayModal.vue";
import EditHolidayModal from "../../Modal/EditHolidayModal.vue";
import { fetchHolidaysData, fetchWorkingDaysData, saveWorkingDaysData } from "../../../services/dataService";

// ── Type helpers ─────────────────────────────────────────────────────────────
const TYPE_PRIORITY = ['poya', 'mercantile', 'public', 'bank'];

function getPrimaryType(types) {
  for (const t of TYPE_PRIORITY) {
    if (types.includes(t)) return t;
  }
  return types[0] || 'public';
}

export default {
  name: 'WorkingDayCalendar',
  components: {
    AddHolidayModal,
    EditHolidayModal
  },

  data() {
    return {
      // Modals state
      showAddHolidayModal: false,
      showEditHolidayModal: false,
      selectedHoliday: null,

      currentDate: new Date(),
      isEditMode: false,
      isSaving: false,
      lastSaved: null,

      // Working days stored as Set of 'YYYY-MM-DD' strings
      workingDays: new Set(),
      initialWorkingDays: new Set(),
      loadedWorkingDayMonths: new Set(),
      editedWorkingDayMonths: new Set(),

      dayLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],

      // ── Holiday data (from Google Sheet) ──
      allHolidays:     [],   // flat array: { year, month, day, name, types[] }
      holidaysLoading: false,
      holidaysError:   null,
      lastRefreshed:   null,
    };
  },

  computed: {
    currentYear()  { return this.currentDate.getFullYear(); },
    currentMonth() { return this.currentDate.getMonth(); }, // 0-based
    currentMonthName() {
      return this.currentDate.toLocaleString('default', { month: 'long' });
    },
    daysInMonth() {
      return new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
    },
    firstDayOfMonth() {
      // getDay() returns 0=Sun … 6=Sat; shift so Monday = column 0
      const day = new Date(this.currentYear, this.currentMonth, 1).getDay();
      return (day + 6) % 7;
    },
    hasDataForYear() {
      return this.allHolidays.some(h => h.year === this.currentYear);
    },

    /**
     * Holidays for the current month — one entry per holiday (no merging).
     * Same-day holidays appear as separate cards in the side panel.
     */
    holidaysThisMonth() {
      const m = this.currentMonth + 1;
      return this.allHolidays
        .filter(h => h.year === this.currentYear && h.month === m)
        .map(h => ({
          id:          h.id,
          date:        this.dateKey(h.day),
          day:         h.day,
          name:        h.name,
          is_active:   h.is_active,
          types:       h.types,
          primaryType: getPrimaryType(h.types),
        }))
        .sort((a, b) => a.day - b.day || a.name.localeCompare(b.name));
    },

    /**
     * Count of unique calendar DAYS that have at least one holiday.
     * Used for the stats bar ("Holidays" counter) so May 1 counts as 1 day,
     * not 2, even if Labour Day + Vesak both fall on it.
     */
    holidayDaysCount() {
      return new Set(this.holidaysThisMonth.filter(h => h.is_active !== false).map(h => h.day)).size;
    },

    workingDaysCount() {
      let count = 0;
      for (let d = 1; d <= this.daysInMonth; d++) {
        if (this.workingDays.has(this.dateKey(d))) count++;
      }
      return count;
    },
    weekendDaysCount() {
      let count = 0;
      for (let d = 1; d <= this.daysInMonth; d++) {
        if (this.isWeekend(d)) count++;
      }
      return count;
    },
  },

  watch: {
    allHolidays()    { this.loadWorkingDays(); }, // re-evaluate once sheet data arrives
  },

  async mounted() {
    await this.fetchHolidays();
    this.loadWorkingDays();
  },

  methods: {
    // ── Navigation ──────────────────────────────────────────────────────────
    prevMonth() {
      const d = new Date(this.currentDate);
      d.setMonth(d.getMonth() - 1);
      this.currentDate = d;
    },
    nextMonth() {
      const d = new Date(this.currentDate);
      d.setMonth(d.getMonth() + 1);
      this.currentDate = d;
    },

    // ── Edit Mode ────────────────────────────────────────────────────────────
    openEditModal(holiday) {
      this.selectedHoliday = holiday;
      this.showEditHolidayModal = true;
    },
    handleModalClose() {
      this.showAddHolidayModal = false;
      this.showEditHolidayModal = false;
      this.selectedHoliday = null;
      document.body.style.overflow = 'auto';
      this.refreshHolidays();
    },
    handleModalSave(data) {
      // Note: Here you can insert API requests to save/update to Google Sheet or DB
      console.log('Saved holiday data:', data);
      document.body.style.overflow = 'auto';
      this.refreshHolidays();
    },

    enterEditMode() {
      this.isEditMode = true;
      this.initialWorkingDays = new Set(this.workingDays);
    },
    cancelEditMode() {
      this.isEditMode = false;
      this.editedWorkingDayMonths = new Set();
      this.workingDays = new Set(this.initialWorkingDays);
    },
    async saveCalendar() {
      this.isSaving = true;
      
      try {
        // 1. Get the user ID from local storage
        const profileId = parseInt(localStorage.getItem('profile_id')) || null;

        // 2. Group all selected working days by Year and Month
        const groups = {};
        
        const allRelevantMonths = new Set([
          ...this.loadedWorkingDayMonths,
          ...this.editedWorkingDayMonths,
          `${this.currentYear}-${this.currentMonth + 1}`
        ]);

        for (const monthKey of allRelevantMonths) {
          const parts = monthKey.split('-');
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          groups[monthKey] = {
            year: year,
            month: month,
            working_days: [],
            created_by: profileId,
            updated_by: profileId
          };
        }
        
        for (const dateKey of this.workingDays) {
          const parts = dateKey.split('-');
          if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);

            const groupKey = `${year}-${month}`;
            
            // If this year/month combo doesn't exist yet, initialize it
            if (!groups[groupKey]) {
              groups[groupKey] = {
                year: year,
                month: month,
                working_days: [],
                created_by: profileId,
                updated_by: profileId
              };
            }
            
            // Add the day to the array
            groups[groupKey].working_days.push(day);
          }
        }

        // 3. Format into a flat array and sort the days chronologically
        const payload = Object.values(groups).map(group => {
          group.working_days.sort((a, b) => a - b);
          return group;
        });
        
        console.log('Sending Payload:', JSON.stringify(payload, null, 2));

        // 4. Send the payload to your API
        const token = localStorage.getItem('token_set');
        const res = await saveWorkingDaysData(payload, token);
        
        if (res.data.status !== 'success') {
          throw new Error(res.data.message || 'Failed to save calendar.');
        }

        // 5. Update UI on success
        const now = new Date();
        this.lastSaved = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        this.isEditMode = false;
        this.toast('success', res.data.message || 'Working days configuration saved successfully.');

        // Redirect to calendar list
        this.$router.push('/working-day-calendar');
        
      } catch (err) {
        console.error('Save failed:', err);
        const errorMsg = err.response?.data?.message || err.message || 'Failed to save. Please try again.';
        this.toast('error', errorMsg);
      } finally {
        this.isSaving = false;
      }
    },


    // ── Working Days ─────────────────────────────────────────────────────────
    dateKey(day) {
      const y = this.currentYear;
      const m = String(this.currentMonth + 1).padStart(2, '0');
      const d = String(day).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    isWorkingDay(day) {
      return this.workingDays.has(this.dateKey(day));
    },
    isWeekend(day) {
      return [0, 6].includes(new Date(this.currentYear, this.currentMonth, day).getDay());
    },
    toggleWorkingDay(day) {
      if (!this.isEditMode) return;
      // Allow holidays and weekends to be toggled as override working days
      const key = this.dateKey(day);
      const updated = new Set(this.workingDays);
      if (updated.has(key)) { updated.delete(key); } else { updated.add(key); }
      this.workingDays = updated;
      this.editedWorkingDayMonths.add(`${this.currentYear}-${this.currentMonth + 1}`);
    },
    async loadWorkingDays() {
      try {
        const token = localStorage.getItem('token');
        const res = await fetchWorkingDaysData(token);
        
        if (res.data.status === 'success') {
          const newSet = new Set();
          const loadedMonthsSet = new Set();
          for (const item of res.data.data) {
            const y = item.year;
            // Pad month with 0 since we store "YYYY-MM-DD"
            const m = String(item.month).padStart(2, '0');
            loadedMonthsSet.add(`${y}-${parseInt(item.month, 10)}`);
            for (const d of item.working_days) {
              const dayStr = String(d).padStart(2, '0');
              newSet.add(`${y}-${m}-${dayStr}`);
            }
          }
          this.workingDays = newSet;
          this.loadedWorkingDayMonths = loadedMonthsSet;
          this.editedWorkingDayMonths = new Set();
        }
      } catch (err) {
        console.error('Failed to load working days:', err);
      }
    },

    // ── API Holiday Loader ──────────────────────────────────────────
    async fetchHolidays() {
      this.holidaysLoading = true;
      this.holidaysError   = null;

      try {
        const token = localStorage.getItem('token_set');
        const res = await fetchHolidaysData(token);
        
        if (res.data.status === 'success') {
          const raw = res.data.data.map(row => {
            const types = [];
            if (row.is_public) types.push('public');
            if (row.is_bank) types.push('bank');
            if (row.is_mercantile) types.push('mercantile');
            if (row.is_poya) types.push('poya');
            return {
              id: row.id,
              year: parseInt(row.year),
              month: parseInt(row.month),
              day: parseInt(row.day),
              name: row.holiday,
              is_active: row.is_active !== 0 && row.is_active !== '0' && row.is_active !== false,
              types: types.length ? types : ['public'],
            };
          }).filter(h => h.year && h.month && h.day && h.name);

          this.allHolidays = raw;
          this.lastRefreshed = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
            throw new Error(res.data.message || 'Failed to load holidays.');
        }

      } catch (err) {
        console.error('[WorkingDayCalendar] Failed to load holidays:', err);
        this.holidaysError = err.message || 'Could not load holiday data from API.';
      } finally {
        this.holidaysLoading = false;
      }
    },

    async refreshHolidays() {
      await this.fetchHolidays();
      this.loadWorkingDays();
    },

    // ── Holidays ─────────────────────────────────────────────────────────────
    /**
     * Returns a combined holiday object for a given day (for calendar cell coloring).
     * Unions the types of all holidays on that day so the cell shows the correct
     * primary type even when multiple holidays fall on the same date.
     */
    getHoliday(day) {
      const dayEntries = this.holidaysThisMonth.filter(h => h.day === day && h.is_active !== false);
      if (!dayEntries.length) return null;
      const allTypes = [...new Set(dayEntries.flatMap(h => h.types))];
      return {
        day,
        types:       allTypes,
        primaryType: getPrimaryType(allTypes),
      };
    },
    getHolidayIcon(type) {
      return { poya: '🌙', public: '🏛️', bank: '🏦', mercantile: '🛍️' }[type] || '📅';
    },
    formatHolidayDate(day) {
      return new Date(this.currentYear, this.currentMonth, day)
        .toLocaleDateString('en-LK', { weekday: 'short', day: 'numeric', month: 'short' });
    },

    // ── Cell Classes ──────────────────────────────────────────────────────────
    getDayClasses(day) {
      const holiday = this.getHoliday(day);
      const weekend = this.isWeekend(day);
      const working = this.isWorkingDay(day);
      // Working always wins — any day marked working shows as green
      return [
        'cal-cell',
        {
          'cal-cell-working': working,
          'cal-cell-weekend': weekend && !working,
          'cal-cell-off':     !working && !holiday && !weekend,
          'cal-cell-holiday': !!holiday && !working,
          [`cal-cell-holiday-${holiday?.primaryType}`]: !!holiday && !working,
          'cal-cell-today':     this.isToday(day),
          'cal-cell-clickable': this.isEditMode,
        },
      ];
    },
    isToday(day) {
      const now = new Date();
      return now.getDate() === day
        && now.getMonth() === this.currentMonth
        && now.getFullYear() === this.currentYear;
    },
    scrollToDay(day) {
      const cells = document.querySelectorAll('.cal-cell');
      cells.forEach(c => c.classList.remove('cal-cell-highlight'));
      const target = cells[this.firstDayOfMonth + day - 1];
      if (target) {
        target.classList.add('cal-cell-highlight');
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setTimeout(() => target.classList.remove('cal-cell-highlight'), 1500);
      }
    },
    toast(type, message) {
      const t = useToast();
      type === 'error'
        ? t.error(message, { position: 'top-right', timeout: 3000 })
        : t.success(message, { position: 'top-right', timeout: 3000 });
    },
  },
};
</script>

<style scoped>
/* ── Layout ────────────────────────────────────────────────────────────── */
.card-header {
  background: #fff;
  border-bottom: 1px solid #dee2e6;
  padding: 14px 20px;
}

.calendar-body-grid {
  display: grid;
  grid-template-columns: 1fr 270px;
  min-height: 560px;
}

/* ── Calendar Panel ─────────────────────────────────────────────────────── */
.calendar-panel {
  padding: 20px;
  border-right: 1px solid #dee2e6;
}

/* ── Nav Buttons ────────────────────────────────────────────────────────── */
.nav-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

/* ── Stats ──────────────────────────────────────────────────────────────── */
.stats-row {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px 16px;
}
.stat-item {
  flex: 1;
  text-align: center;
}
.stat-divider {
  width: 1px;
  height: 28px;
  background: #dee2e6;
  flex-shrink: 0;
}
.stat-val {
  display: block;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 3px;
}
.stat-label {
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ── Day Labels ─────────────────────────────────────────────────────────── */
.day-labels-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.day-label {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 3px 0;
}
.day-label-weekend { color: #e6a817; }

/* ── Calendar Grid ──────────────────────────────────────────────────────── */
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}
.cal-cell {
  position: relative;
  aspect-ratio: 1;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: 1.5px solid transparent;
  transition: all 0.12s;
  cursor: default;
  min-width: 0;
}
.cal-cell-clickable {
  cursor: pointer;
}
.cal-cell-clickable:hover {
  transform: scale(1.06);
  z-index: 1;
  box-shadow: 0 0 0 2px #0d6efd;
}

.day-num {
  font-size: 12px;
  font-weight: 500;
  color: #343a40;
  line-height: 1;
}
.holiday-icon {
  font-size: 11px;
  line-height: 1;
}
.working-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #198754;
}

/* Cell states */
.cal-cell-working {
  background: #d1e7dd;
  border-color: #a3cfbb;
}
.cal-cell-working .day-num { color: #0f5132; font-weight: 600; }

.cal-cell-weekend {
  background: #fff3cd;
  border-color: #ffc107;
}
.cal-cell-weekend .day-num { color: #856404; }

.cal-cell-off {
  background: #f8f9fa;
  border-color: #e9ecef;
}
.cal-cell-off .day-num { color: #adb5bd; }

/* Holiday cells */
.cal-cell-holiday-poya {
  background: #ede7f6;
  border-color: #ce93d8;
}
.cal-cell-holiday-poya .day-num { color: #6a1b9a; font-weight: 600; }

.cal-cell-holiday-public {
  background: #fce4ec;
  border-color: #f48fb1;
}
.cal-cell-holiday-public .day-num { color: #880e4f; font-weight: 600; }

.cal-cell-holiday-bank {
  background: #e3f2fd;
  border-color: #90caf9;
}
.cal-cell-holiday-bank .day-num { color: #0d47a1; font-weight: 600; }

.cal-cell-holiday-mercantile {
  background: #fce4ec;
  border-color: #ef9a9a;
}
.cal-cell-holiday-mercantile .day-num { color: #b71c1c; font-weight: 600; }

.cal-cell-today {
  outline: 2px solid #0d6efd;
  outline-offset: 1px;
}
.cal-cell-empty {
  background: transparent;
  border: none;
  cursor: default;
}

@keyframes highlight-pulse {
  0% { box-shadow: 0 0 0 0 rgba(13,110,253,.5); }
  70% { box-shadow: 0 0 0 8px rgba(13,110,253,0); }
  100% { box-shadow: 0 0 0 0 rgba(13,110,253,0); }
}
.cal-cell-highlight { animation: highlight-pulse 0.6s ease 2; }


/* ── Legend ─────────────────────────────────────────────────────────────── */
.legend-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  border-top: 1px solid #dee2e6;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #6c757d;
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}


/* ── Google Sheet Status ─────────────────────────────────────────────────── */
.sheet-notice {
  font-size: 12px;
  color: #495057;
  background: #e9ecef;
  border: 1px dashed #ced4da;
  border-radius: 6px;
  padding: 10px 12px;
  text-align: center;
}
.sheet-notice code {
  background: #dee2e6;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
}
.sheet-error {
  font-size: 12px;
  color: #842029;
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 6px;
  padding: 10px 12px;
  text-align: center;
}
.refresh-btn {
  font-size: 12px;
  color: #6c757d;
  line-height: 1;
  text-decoration: none !important;
}
.refresh-btn:hover { color: #0d6efd; }

/* ── Side Panel ─────────────────────────────────────────────────────────── */
.side-panel {
  padding: 16px;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
.side-section-title {
  font-size: 11px;
  font-weight: 700;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

/* ── Holiday Cards ──────────────────────────────────────────────────────── */
.holiday-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.holiday-card {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.12s;
}
.holiday-card:hover { transform: translateX(2px); }

.holiday-card-poya       { background: #EAEFEF; border-color: #BFC9D1; }
.holiday-card-public     { background: #EAEFEF; border-color: #BFC9D1; }
.holiday-card-bank       { background: #EAEFEF; border-color: #BFC9D1; }
.holiday-card-mercantile { background: #EAEFEF; border-color: #BFC9D1; }

.holiday-card-inactive   { background: #ffebee !important; border-color: #ffcdd2 !important; }
.holiday-card-inactive .holiday-name { color: #d32f2f !important; }

.holiday-info { flex: 1; min-width: 0; }
.holiday-name { font-size: 12px; font-weight: 600; color: #212529; line-height: 1.3; }
.holiday-date { font-size: 11px; color: #6c757d; margin-top: 2px; }
.holiday-types-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  justify-content: center;
  line-height: 1;
  font-size: 9px;
}

/* ── Holiday type emoji row (side panel) ──────────────────────────────── */
.holiday-type-icons { display: flex; gap: 4px; }
.htype-emoji { font-size: 12px; line-height: 1; }

/* ── Summary ────────────────────────────────────────────────────────────── */
.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}
.summary-item {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.summary-label { font-size: 10px; color: #6c757d; }
.summary-val { font-size: 18px; font-weight: 700; color: #212529; }


/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .calendar-body-grid {
    grid-template-columns: 1fr;
  }
  .calendar-panel {
    border-right: none;
    border-bottom: 1px solid #dee2e6;
    padding: 14px;
  }
  .side-panel { padding: 14px; }
  .cal-cell { border-radius: 4px; }
  .day-num { font-size: 11px; }
  .card-header { padding: 10px 14px; }
}

</style>
